import { ref } from 'vue';
import type { Ref } from 'vue';
import type {
	BookingTypeFilter,
	ResourcePlanningGantt,
	ResourcePlanningItem,
	BookingsWithoutResource,
} from '../../types';
import type { ResourcePlanningGanttConfig } from '../../components/ResourcePlanningGantt/ResourcePlanningGanttConfig';
import { isBookingVisible } from '../../components/ResourcePlanningGantt/ResourcePlanningGanttUtils';
import { BOOKING_VISIBLE_PROJECT_ID } from '../../constants';

type OrphanedBooking = BookingsWithoutResource[string];

export interface OrphanedBookingCallbacks<T = OrphanedBooking> {
	/**
	 * Optional callback to execute before adding the booking to the chart.
	 * Use this to perform operations like extending project visible dates
	 * or push the booking to a typed array.
	 *
	 * The booking parameter is cast to type T, allowing call sites to narrow
	 * the type based on their specific context (e.g., Portfolio bookings with
	 * project field vs Chart bookings without).
	 */
	beforeAdd?: (booking: T, item: ResourcePlanningItem) => void;

	/**
	 * Optional callback to determine the project ID for visibility checks.
	 * If not provided, defaults to BOOKING_VISIBLE_PROJECT_ID.
	 */
	getProjectId?: () => string | undefined;
}

/**
 * Composable for managing bookings that arrive via WebSocket before their parent
 * resources are loaded into the Gantt chart.
 *
 * @param config - ResourcePlanningGanttConfig instance
 * @param gantt - Gantt chart instance
 * @param bookingTypeFilter - Reactive booking type filter (fixed/tentative/both)
 * @param callbacks - Optional callbacks for customizing behavior
 * @returns addOrphanedBooking and processOrphanedBookings functions
 */
export const useResourcePlanningOrphanedBookings = <T = OrphanedBooking>(
	config: ResourcePlanningGanttConfig,
	gantt: ResourcePlanningGantt,
	bookingTypeFilter: Ref<BookingTypeFilter>,
	callbacks: OrphanedBookingCallbacks<T> = {}
) => {
	const orphanedBookings = ref<BookingsWithoutResource>({});

	const addOrphanedBooking = (booking: OrphanedBooking) => {
		orphanedBookings.value[booking.id] = booking;
	};

	const processOrphanedBookings = () => {
		const bookingsToProcess = Object.values(orphanedBookings.value);

		bookingsToProcess.forEach((booking) => {
			const item = config.bookingToChartItem(booking);

			if (item.parent && gantt.isTaskExists(item.parent)) {
				const projectId =
					callbacks.getProjectId?.() ?? BOOKING_VISIBLE_PROJECT_ID;
				const isVisible = isBookingVisible(
					bookingTypeFilter.value,
					booking.isTentative,
					projectId
				);

				if (isVisible) {
					callbacks.beforeAdd?.(booking as T, item);
					config.addItemToChart(item);
					delete orphanedBookings.value[booking.id];
				}
			}
		});
	};

	return {
		addOrphanedBooking,
		processOrphanedBookings,
	};
};
